import type { AppointmentStatus, GroomingStatus } from "@/hooks/useAppointments";

const LEGACY_APPOINTMENT_STATUS_VALUES = [
  "agendado",
  "confirmado",
  "em_atendimento",
  "finalizado",
  "cancelado",
  "faltou",
  "pago",
] as const;

const LEGACY_GROOMING_STATUS_VALUES = [
  "agendado",
  "confirmado",
  "no_banho",
  "na_tosa",
  "secando",
  "finalizado",
  "entregue",
  "cancelado",
  "faltou",
  "pago",
] as const;

export const isAppointmentStatusValue = (value: unknown): value is AppointmentStatus =>
  typeof value === "string" && (LEGACY_APPOINTMENT_STATUS_VALUES as readonly string[]).includes(value);

export const isGroomingStatusValue = (value: unknown): value is GroomingStatus =>
  typeof value === "string" && (LEGACY_GROOMING_STATUS_VALUES as readonly string[]).includes(value);

const GROOMING_ONLY_STATUSES = new Set(["no_banho", "na_tosa", "secando", "entregue"]);

export const GROOMING_STATUS_LABELS: Record<string, string> = {
  agendado: "Agendado",
  confirmado: "Confirmado",
  no_banho: "No Banho",
  na_tosa: "Na Tosa",
  secando: "Secando",
  finalizado: "Finalizado",
  entregue: "Entregue",
  pago: "Pago",
  cancelado: "Cancelado",
  faltou: "Faltou",
  em_atendimento: "Em Atendimento",
};

export const getGroomingStatusLabel = (status: string | null | undefined): string =>
  (status && GROOMING_STATUS_LABELS[status]) || status || "";

export const mapGroomingStatusToAppointmentStatus = (status: GroomingStatus): AppointmentStatus => {
  if (GROOMING_ONLY_STATUSES.has(status)) return "em_atendimento";
  if (status === "pago") return "finalizado";
  return status as AppointmentStatus;
};

/**
 * Resolves the unified appointment status to persist for a banho_tosa appointment.
 *
 * Since `grooming_status` was merged into `appointments.status`, the selected
 * grooming status name (e.g. "no_banho", "na_tosa", "secando") IS already a valid
 * appointment status and must be persisted as-is. Only statuses configured as a
 * final flow (finalizado/cancelado) collapse to their final_type.
 *
 * This must NOT run grooming statuses through `coerceAppointmentStatusForNonGrooming`,
 * which would flatten them to "em_atendimento".
 */
export const resolveGroomingAppointmentStatus = (
  groomingStatusName: string,
  finalType: string | null | undefined,
): AppointmentStatus => {
  if (finalType === "finalizado" || finalType === "cancelado") {
    return finalType as AppointmentStatus;
  }
  return groomingStatusName as AppointmentStatus;
};

export const coerceAppointmentStatusForNonGrooming = (
  value: unknown,
  fallback = "agendado",
): AppointmentStatus => {
  if (value === "pago") return "finalizado";
  if (typeof value === "string" && GROOMING_ONLY_STATUSES.has(value)) return "em_atendimento";
  if (typeof value === "string" && value.trim() !== "") return value as AppointmentStatus;
  return fallback as AppointmentStatus;
};
